import {useCallback, useEffect, useMemo, useReducer, useRef, useState} from 'react'
import {useApi} from "@webng/react-app-common";
import {ApiClient, isApiResponseError} from "@webng/tickaroo-api";
import {IEvent, IGameShowResponse} from "@webng-types/write-model";
import {LiveblogData, LiveblogDataReducer, liveblogDataReducer} from "./liveblogDataReducer";
import {CancelablePromiseType} from "cancelable-promise";
import {RefreshType} from "@webng-types/embedjs";
import {TrackingFunction, findLoadMoreBottomEventId, findLoadMoreTopEventId, legacyGameShowResponseConverter} from "@webng/liveblog";
import { useVersions } from '../../utils/useVersions';
import {useQuery, useQueryClient} from '@tanstack/react-query';
import { useChangeBridgeContext } from '@webng/liveblog/src/change-stream/useChangeBridge';


const initialReducerState: LiveblogData = {
  game: {
    _type: "Tik::Model::Game",
    sportstype: "news"
  },
  events: {},
}

type LoadEventTypes = "l_ini" | "l_mor" | "l_rfs"

interface TickerShowQuery {
  id: string
  limit?: number
  skip?: number
  include_milestone_events?: true,
  include_goal_events?: true
  include_highlight_events?: true
  reverse?: boolean,
  limit_until_event_id?: string
  limit_until_event_id_offset?: number
  continue_event_id?: string
  include_continue_event?: boolean
  tags?: string
  refresh?: number
  cacheBreaker?: number
}

interface UseLiveblogDataProps {
  id: string,
  limit: number
  deepLinkLimit: number
  deepLinkDepth: number
  reverse: boolean
  trackingFunction: TrackingFunction
  refresh: RefreshType
  initialData?: IGameShowResponse
  isSSR?: boolean
}

function reversGameShowResponse(r: IGameShowResponse): IGameShowResponse {
  if(r.game?.events && r.game?.summary) {
    return {
      ...r,
      game: {
        ...r.game,
        summary: {
          ...r.game?.summary,
          filtered_first_event: r.game.summary.filtered_last_event,
          filtered_last_event: r.game.summary.filtered_first_event,
        },
        events: [...r.game.events].reverse()
      }
    }
  } else {
    return r
  }
}

export function useLiveblogData({id, limit, deepLinkLimit, deepLinkDepth, trackingFunction, refresh, reverse, initialData, isSSR}: UseLiveblogDataProps) {
  const apiClient = useApi()
  const [error, setError] = useState<number|undefined>(undefined)

  const [tags, setTags] = useState<string[]|undefined>(undefined)

  const [isLoadingBackToLive, setIsLoadingBackToLive] = useState<boolean>(false)
  const [isLoadingMoreTop, setIsLoadingMoreTop] = useState<boolean>(false)
  const [isLoadingMoreBottom, setIsLoadingMoreBottom] = useState<boolean>(false)
  const [isLoadingPendingUpdate, setIsLoadingPendingUpdate] = useState<boolean>(false)
  const [isLoadingSortChange, setIsLoadingSortChange] = useState<boolean>(false)

  // Store the previous value of reverse to detect changes
  const previousReverseRef = useRef<boolean>(reverse)

  const [data, dispatch] = useReducer<LiveblogDataReducer, IGameShowResponse|undefined>(liveblogDataReducer, initialData, (r: IGameShowResponse|undefined) => {
    if(r) {
      if(reverse) { // this is a hacky way to make reverse work by processing it in the right order and reversing it again for the output
        return liveblogDataReducer(initialReducerState, {type: 'init', response: reversGameShowResponse(r)})
      } else {
        return liveblogDataReducer(initialReducerState, {type: 'init', response: r})
      }
    } else {
      return initialReducerState
    }
  })

  const makeStatefulCall = useCallback((query: TickerShowQuery) => {
    if(query.tags === undefined && tags) {
      query = {...query, tags: tags.join()}
    }
    return apiClient.getJson(ApiClient.API_V6_TICKER_SHOW, query).then(legacyGameShowResponseConverter).then((r: IGameShowResponse) => {
      if(query.reverse) { // this is a hacky way to make reverse work by processing it in the right order and reversing it again for the output
        r = reversGameShowResponse(r)
      }
      return r;
    }).catch(e => {
      if(e && e.status !== 422) {
        trackingFunction({t: 'l_err', s: 0})
      }
      throw e
    })
  }, [apiClient, trackingFunction, tags])

  const currentRefresh = useRef<CancelablePromiseType<IGameShowResponse>>();
  const makeRefreshCall = useCallback((id: string, version: number, _version?: number) => {
    if(currentRefresh.current) {
      currentRefresh.current.cancel()
    }
    currentRefresh.current = makeStatefulCall( _version ? {id: id, refresh: version, cacheBreaker: _version} : {id: id, refresh: version} )
    return currentRefresh.current
  }, [makeStatefulCall])

  const reinitializeData = useCallback((eventId: string|undefined) => {
    const query: TickerShowQuery = {
      id: id,
      limit: limit,
      include_milestone_events: true,
      include_goal_events: true,
      include_highlight_events: true,
      skip: -deepLinkDepth,
      reverse
    }
    if (eventId) {
      query['continue_event_id'] = eventId
      query['include_continue_event'] = true
    }
    return makeStatefulCall(query).then(r => {
      trackingFunction({t: 'l_ini'})
      dispatch({type: 'init', response: r})
    })
  }, [makeStatefulCall, trackingFunction, id, limit, deepLinkDepth, reverse])

  const loadMoreTopEventId = useMemo(() => findLoadMoreTopEventId(data.game), [data.game])
  const loadMoreBottomEventId = useMemo(() => findLoadMoreBottomEventId(data.game), [data.game])

  const navigateToEventId = useCallback((eventId: string|undefined, loadType: LoadEventTypes = 'l_mor', dispatchType: 'init'|'more' = 'more') => {
    if (eventId && deepLinkLimit > 0) {
      const query: TickerShowQuery = {
        id: id,
        limit: deepLinkLimit,
        limit_until_event_id: eventId,
        limit_until_event_id_offset: Math.floor(limit/2),
        reverse
      }

      const continueEventId = reverse ? loadMoreTopEventId : loadMoreBottomEventId
      if(continueEventId) {
        query.continue_event_id = continueEventId
      }

      return makeStatefulCall(query)
        .then(r => {
          trackingFunction({t: loadType})
          dispatch({type: dispatchType, response: r})
        })
        .catch(e => {
          if(e.status === 422) {
            return reinitializeData(eventId)
          } else {
            throw e;
          }
        })
    } else {
      return reinitializeData(eventId);
    }
  }, [makeStatefulCall, trackingFunction, reinitializeData, id, reverse, deepLinkLimit, limit, loadMoreTopEventId, loadMoreBottomEventId])

  // initial load
  useEffect(() => {
    if(initialData?.game?.ticker_id !== id) {
      const promise = reinitializeData(undefined).catch(e => {
        if(isApiResponseError(e)) {
          setError(e.status)
        } else {
          setError(0)
        }
      })
      return () => promise.cancel()
    } else {
      // in case initial data from higher level widget, still trigger tracking of init data, but no need to load any
      trackingFunction({t: 'l_ini'})
    }
  }, [reinitializeData, trackingFunction, id, initialData, setError])

  // Re-initialize data when sort order changes
  useEffect(() => {
    if (previousReverseRef.current === reverse) {
      return;
    }

    lastUiUpdateTime.current = Date.now();
    setIsLoadingSortChange(true);
    const promise = reinitializeData(undefined).catch(e => {
      if(isApiResponseError(e)) {
        setError(e.status)
      } else {
        setError(0)
      }
    }).finally(() => {
      previousReverseRef.current = reverse;
      setIsLoadingSortChange(false)
    });

    return () => promise.cancel();
  }, [reverse, reinitializeData, trackingFunction, setError]);

  const loadMoreTop = useMemo(() => {
    if (loadMoreTopEventId) {
        return function () {
          trackingFunction({t: 't_mor', e: loadMoreTopEventId})
          setIsLoadingMoreTop(true)
          return makeStatefulCall({
            id: id,
            limit: limit,
            skip: - (limit + 1),
            continue_event_id: loadMoreTopEventId,
          }).then(r => {
            trackingFunction({t: 'l_mor'})
            dispatch({type: 'more', response: r})
          })
            .finally(() => setIsLoadingMoreTop(false))
        }
    } else {
      return undefined
    }
  }, [makeStatefulCall, id, loadMoreTopEventId, limit, setIsLoadingMoreTop, trackingFunction])

  const loadMoreBottom = useMemo(() => {

    if(loadMoreBottomEventId) {
        return function () {
          trackingFunction({t: 't_mor', e: loadMoreBottomEventId})
          setIsLoadingMoreBottom(true)
          return makeStatefulCall({
            id: id,
            limit: limit,
            continue_event_id: loadMoreBottomEventId,
          }).then(r => {
            trackingFunction({t: 'l_mor'})
            dispatch({type: 'more', response: r})
          }).finally(() => setIsLoadingMoreBottom(false))
        }
    } else {
      return undefined
    }
  }, [makeStatefulCall, id, loadMoreBottomEventId, limit, setIsLoadingMoreBottom, trackingFunction])

  const isAtTop = !loadMoreTop

  const backToTop = useMemo(() => {
    if(!isAtTop) {
      return () => {
        trackingFunction({t: 't_mor'})
        setIsLoadingBackToLive(true)
        navigateToEventId(undefined).finally(() => setIsLoadingMoreTop(false))
      }
    } else {
      return undefined
    }
  }, [navigateToEventId, isAtTop, setIsLoadingBackToLive, trackingFunction])

  const setChangeBridgeLiveblogLocalId = useChangeBridgeContext()?.setLiveblogLocalId

  useEffect(() => {
    if(setChangeBridgeLiveblogLocalId && data.game.local_id) {
      setChangeBridgeLiveblogLocalId(data.game.local_id)
    }
  }, [data.game.local_id, setChangeBridgeLiveblogLocalId])

  const versionNumber = useVersions(trackingFunction)

  const queryClient = useQueryClient()
  // set initial data so first poll occurs after the first interval
  useMemo(() => initialData && queryClient.setQueryData(["refresh", initialData.game!.version!, undefined], initialData, {}), [initialData, queryClient])

  const { data: refreshData, isSuccess} = useQuery<IGameShowResponse, any, IGameShowResponse, ["refresh", number, number|undefined]>({
    queryKey: ["refresh", data.game.version!, versionNumber],
    queryFn: async ({queryKey}) => {
      const [, version, _version] = queryKey
      trackingFunction({t: 'l_rfs'})
      return makeRefreshCall(id, version, _version)
    },
    enabled: refresh !== "off" && data.game.version !== versionNumber && isAtTop,
    retry: true,
    // when versionNumber is undefined, we can't use the websocket connection to get version number updates
    // therefore we have to poll every 20 seconds (default old behavior)
    // when versionNumber is defined, we can use the websocket connection to get version number updates
    // then we rely on the query key to rerun the query when the supplied version number changes
    refetchInterval: (query) => {
      if(query.queryKey[2] === undefined) {
        return 20000;
      } else {
        return false;
      }
    },
    refetchIntervalInBackground: false,
    staleTime: Infinity
  })


  // we store the timestamp of the last time the ui was updated by init or user action
  // if a subsequent update is available within a grace time we apply it immediatly
  const initTime = useRef<number>(Date.now())
  const lastUiUpdateTime = useRef<number>(Date.now())

  // tanstack has no onSuccess callback
  // therefore we use useEffect to dispatch the version update
  useEffect(() => {
    if(refreshData && isSuccess && data.game.version) {
      if (refreshData.game!.version! > data.game.version) {

        if(refresh === 'auto') {
          dispatch({type: 'applyUpdate', response: refreshData})
        } else if(Date.now() - initTime.current < (isSSR ? 1500 : 1000) || Date.now() - lastUiUpdateTime.current < 400) {
          // within this grace time we update immediately again, not setting pending updates
          // Grace Time Research: https://chatgpt.com/share/e/68257d56-d028-8010-a9cd-06afd30ed79d
          // ideally value should be around 500ms but not exceed 1s
          // Values should not exceed 1s
          // initially we use a grace time on the high end since the website might still
          // be perceived as loading anyway
          // if SSR is used we go even over the max because we're still going to be
          // faster than all the ads anyway ;)
          dispatch({type: 'applyUpdate', response: refreshData})
        } else {
          dispatch({type: 'setPendingUpdate', response: refreshData})
        }
      }

    }
  }, [refreshData, isSuccess, data.game.version, refresh, trackingFunction, dispatch, isSSR])

  const applyPendingUpdate = useCallback(() => {
    trackingFunction({t: 't_upd'})
    setIsLoadingPendingUpdate(true)
    dispatch({type: 'applyPendingUpdate'})
    setIsLoadingPendingUpdate(false)
    lastUiUpdateTime.current = Date.now()
  }, [dispatch, setIsLoadingPendingUpdate, trackingFunction])

  const rows = useMemo(() => {
    if(data.game.events) {
      const ret = data.game.events.map(e => {
        return data.events[e.local_id!] || e
      }).filter(e => e.local_status === 0)
      if(reverse) {
        ret.reverse()
        ret.reverse = function() { throw new Error('double reverse!')}
      }
      return  ret;
    } else {
      return []
    }
  }, [data, reverse])

  const milestones = useMemo(() => {
    if(data.game?.summary?.milestones?.refs) {
      return data.game?.summary?.milestones?.refs.map(ref => data.events[ref.local_id!]).filter(e => !!e) as IEvent[]
    } else {
      return undefined
    }
  }, [data.game?.summary?.milestones?.refs, data.events])

  const goals = useMemo(() => {
    if(data.game?.summary?.goals?.refs) {
      return data.game?.summary?.goals?.refs.map(ref => data.events[ref.local_id!]).filter(e => !!e) as IEvent[]
    } else {
      return undefined
    }
  }, [data.game?.summary?.goals?.refs, data.events])

  const highlights = useMemo(() => {
    if(data.game?.summary?.highlights?.refs) {
      return data.game?.summary?.highlights?.refs.map(ref => data.events[ref.local_id!]).filter(e => !!e).filter(e => tags ? e!.tag_ids?.find(t => tags.includes(t)) : true) as IEvent[]
    } else {
      return undefined
    }
  }, [data.game?.summary?.highlights?.refs, data.events, tags])

  const setSelectedTags = useCallback((tagIds: string[]|undefined) => {
    setTags((tagIds?.length ?? 0) > 0 ? tagIds : undefined);
    const query: TickerShowQuery = {
      id: id,
      limit: limit,
      include_milestone_events: true,
      include_goal_events: true,
      include_highlight_events: true,
      skip: -deepLinkDepth,
      reverse,
      tags: tagIds?.join() || ""
    }
    return makeStatefulCall(query).then(r => dispatch({type: 'init', response: r}))
  }, [setTags, id, limit, deepLinkDepth, reverse, makeStatefulCall])

  return {
    error,
    ...data,
    rows,
    milestones,
    goals,
    highlights,
    navigateToEventId,
    selectedTags: tags,
    setSelectedTags,
    applyPendingUpdate,
    // using previous reverse to determine the correct loadMore functions
    loadMoreTop: previousReverseRef.current ? loadMoreBottom : loadMoreTop,
    loadMoreBottom: previousReverseRef.current ? loadMoreTop : loadMoreBottom,
    backToTop: previousReverseRef.current ? undefined : backToTop,

    isLoadingBackToLive,
    isLoadingMoreTop,
    isLoadingMoreBottom,
    isLoadingPendingUpdate,
    isLoadingSortChange
  }
}
